import type {
    LayoutAlign,
    LayoutComponent,
    LayoutConfig,
    LayoutGridComponent,
    LayoutStyle,
} from '../../../models/layout-estimate-render-config';
import { DEFAULT_GAP, DEFAULT_ROW_HEIGHT, flexAlign, sheetGeometry, trackToLine, type SheetGeometry } from './layout-geometry';
import { resolveFontFamily } from './layout-fonts';
import { parseMarkdown, type BlockNode } from './layout-markdown';

export type CssStyle = Record<string, string>;

export type CopyLookup = (key: string | undefined) => string;

interface ViewBase {
    id: string;
    style: CssStyle;
}

export interface GridView extends ViewBase {
    type: 'grid';
    children: ViewNode[];
}

export interface TextView extends ViewBase {
    type: 'text';
    text: string;
    contentStyle: CssStyle;
}

export interface MarkdownView extends ViewBase {
    type: 'markdown';
    blocks: BlockNode[];
    contentStyle: CssStyle;
}

export interface ImageView extends ViewBase {
    type: 'image';
    src: string;
    alt: string;
    contentStyle: CssStyle;
}

export type ViewNode = GridView | TextView | MarkdownView | ImageView;

export interface SheetView {
    id: string;
    name?: string;
    header?: GridView;
    footer?: GridView;
    body: GridView;
}

export interface LayoutView {
    geometry: SheetGeometry;
    sheets: SheetView[];
}

export function safeCss(value: unknown): string | undefined {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : undefined;
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();

    if (!trimmed || /[;{}<>\\]|url\s*\(|image-set|expression|@import|\/\*/i.test(trimmed)) {
        return undefined;
    }

    return trimmed;
}

const px = (value: unknown): string | undefined => {
    const css = safeCss(value);

    return css === undefined ? undefined : /^-?\d+(\.\d+)?$/.test(css) ? `${css}px` : css;
};

function styleOf(style: LayoutStyle | undefined): CssStyle {
    const out: CssStyle = {};

    if (!style) {
        return out;
    }

    const set = (property: string, value: string | undefined) => {
        if (value !== undefined) {
            out[property] = value;
        }
    };

    set('background', safeCss(style.background));
    set('color', safeCss(style.color));
    set('font-size', px(style.fontSize));
    set('font-weight', safeCss(style.fontWeight));
    set('padding', px(style.padding));
    set('border-radius', px(style.radius));

    const family = safeCss(style.fontFamily);

    set('font-family', family === undefined ? undefined : resolveFontFamily(family));

    if (typeof style.borderWidth === 'number' && style.borderWidth > 0) {
        out['border'] = `${style.borderWidth}px solid ${safeCss(style.borderColor) ?? '#e7e7e7'}`;
    }

    return out;
}

interface Inherited {
    align: LayoutAlign;
    justify: LayoutAlign;
}

const TEXT_ALIGN: Record<LayoutAlign, string> = { start: 'left', center: 'center', end: 'right', stretch: 'left' };

function buildNode(component: LayoutComponent, inherited: Inherited, lookup: CopyLookup): ViewNode {
    const align = component.align ?? inherited.align;
    const justify = component.justify ?? inherited.justify;
    const style: CssStyle = {
        'grid-column': trackToLine(component.col) ?? '1 / -1',
        'grid-row': trackToLine(component.row) ?? 'auto',
        ...styleOf(component.style),
    };

    if (component.type === 'grid') {
        const rowHeight = positive(component.rowHeight, DEFAULT_ROW_HEIGHT);
        const gap = nonNegative(component.gap, DEFAULT_GAP);

        style['grid-auto-rows'] = `${rowHeight}px`;
        style['gap'] = `${gap}px`;

        if (typeof component.height === 'number') {
            style['height'] = `${component.height}px`;
            style['align-self'] = flexAlign(align);
        }

        const children = (component.children ?? []).map((child) => buildNode(child, { align, justify }, lookup));

        return { id: component.id, type: 'grid', style, children };
    }

    style['align-items'] = flexAlign(align);
    style['justify-content'] = flexAlign(justify);

    const contentStyle: CssStyle =
        justify === 'stretch' ? { flex: '1 1 auto', 'text-align': 'left' } : { flex: 'none', 'max-width': '100%', 'text-align': TEXT_ALIGN[justify] };

    switch (component.type) {
        case 'text':
            return { id: component.id, type: 'text', style, contentStyle, text: lookup(component.text) };
        case 'markdown':
            return { id: component.id, type: 'markdown', style, contentStyle, blocks: parseMarkdown(lookup(component.markdown)) };
        case 'image': {
            const fixed = component.sizing === 'fixed';
            const imageStyle: CssStyle = {
                'object-fit': component.fit ?? 'contain',
                'max-width': '100%',
                'max-height': '100%',
                ...(fixed
                    ? { width: `${positive(component.width, 120)}px`, height: `${positive(component.height, 48)}px` }
                    : { width: '100%', height: '100%' }),
            };

            return {
                id: component.id,
                type: 'image',
                style,
                contentStyle: imageStyle,
                src: lookup(component.src),
                alt: lookup(component.alt),
            };
        }
        default:
            return { id: (component as LayoutComponent).id, type: 'text', style, contentStyle, text: '' };
    }
}

function buildBand(band: LayoutGridComponent | undefined, lookup: CopyLookup): GridView | undefined {
    if (!band) {
        return undefined;
    }

    const view = buildNode({ ...band, col: undefined, row: undefined }, { align: 'start', justify: 'start' }, lookup) as GridView;

    view.style['align-self'] = 'stretch';

    return view;
}

export function buildLayoutView(layout: LayoutConfig, lookup: CopyLookup): LayoutView {
    const geometry = sheetGeometry(layout.page, layout.header, layout.footer);
    const root: Inherited = { align: 'start', justify: 'start' };

    const sheets = (layout.pages ?? []).map((page): SheetView => {
        const body: GridView = {
            id: `${page.id}__sheet`,
            type: 'grid',
            style: {
                'grid-auto-rows': `${geometry.rowHeight}px`,
                gap: `${geometry.gap}px`,
                height: `${geometry.bodyHeight}px`,
            },
            children: (page.components ?? []).map((component) => buildNode(component, root, lookup)),
        };

        return {
            id: page.id,
            name: page.name,
            header: buildBand(layout.header, lookup),
            footer: buildBand(layout.footer, lookup),
            body,
        };
    });

    return { geometry, sheets };
}

function positive(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function nonNegative(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}
